'use client';

import { Children, isValidElement, useMemo, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Link from 'next/link';
import { TelemarkMark } from '@icons';
import BlurbList from '@commons/BlurbList/BlurbList';
import Blurb from '@commons/Blurb/Blurb';
import HtmlToJsx from '@commons/HtmlToJsx/HtmlToJsx';
import { useSiteConfig } from '@commons/SiteConfig';
import { mentionedPostsForMessage } from './chatbotMentions';
import stabilizeStreamingMarkdown from './streamingMarkdown';

/**
 * Link renderer for markdown: internal links via next/link, external in a new
 * tab. Plain links only — bookable posts are surfaced through the response
 * blurb carousel beneath the answer, not as inline booking links.
 *
 * A link the assistant invented (see buildDeadLinkCheck) renders as plain text.
 * The visitor still reads the name in the sentence, but is never sent to a 404 —
 * and since the name is usually right, losing the link is the smaller harm.
 * @param {{ href?: string, children?: import('react').ReactNode, isDead?: (href: string) => boolean }} props
 */
function MarkdownLink({ href, children, isDead }) {
  if (!href) return <>{children}</>;
  if (isDead?.(href)) return <span className="Chatbot__unlinked">{children}</span>;

  const isExternal = /^https?:\/\//.test(href);
  return isExternal ? (
    <a href={href} target="_blank" rel="external noopener noreferrer">
      {children}
    </a>
  ) : (
    <Link href={href}>{children}</Link>
  );
}

/**
 * Paragraph renderer for markdown. The model writes section titles as a bolded
 * line of its own ("**Turer i Telemark**"), which markdown turns into a
 * paragraph whose single child is a `strong`. Those become real headings; a
 * paragraph that merely *starts* with bold text ("**Ord** och inga visor") stays
 * a paragraph. CSS can't tell the two apart — `:only-child` counts element
 * siblings, and the trailing text node is invisible to selectors — so the check
 * has to happen here, where the text sibling is visible.
 *
 * The block still being streamed is exempt. A list item written as
 * `**[Gaustatoppen](/x)** – beskrivelse` holds nothing but the bold link for the
 * few tokens before the dash arrives, and promoting it there puts the name in
 * the display face at 700 and then drops it back to body text — the biggest
 * flicker in a streamed answer. `openFrom` carries the offset at which the open
 * block begins; a paragraph that reaches it is still being written.
 * @param {{ children?: import('react').ReactNode, node?: object, openFrom?: { current: number|null } }} props
 */
function MarkdownParagraph({ children, node, openFrom }) {
  const parts = Children.toArray(children).filter((child) => typeof child !== 'string' || child.trim());
  const only = parts.length === 1 ? parts[0] : null;
  const openAt = openFrom?.current;
  const stillOpen = typeof openAt === 'number' && (node?.position?.end?.offset ?? 0) >= openAt;

  if (!stillOpen && isValidElement(only) && only.type === 'strong') {
    return <h4>{only.props.children}</h4>;
  }

  return <p>{children}</p>;
}

/**
 * Table renderer for markdown. Rare, but an answer that is really a matrix
 * (opening hours, prices) gets one — and a table has a minimum width the bubble
 * on a phone does not, so it scrolls inside its own wrapper rather than pushing
 * the whole conversation sideways.
 * @param {{ children?: import('react').ReactNode }} props
 */
function MarkdownTable({ children }) {
  return (
    <div className="Chatbot__text-table">
      <table>{children}</table>
    </div>
  );
}

/**
 * react-markdown element map. Memoised rather than module-level because the link
 * renderer needs the dead-link check, which depends on the loaded collection —
 * the reference still has to stay stable, or the answer remounts (and flickers)
 * on every streamed token.
 * `openFrom` is a ref rather than the offset itself for the same reason: the
 * offset changes with every token, and threading it through as a value would
 * rebuild this map — and remount the answer — on each one.
 * @param {(href: string) => boolean} [isDead]
 * @param {{ current: number|null }} openFrom
 * @returns {import('react-markdown').Components}
 */
function useMarkdownComponents(isDead, openFrom) {
  return useMemo(
    () => ({
      a: (props) => <MarkdownLink {...props} isDead={isDead} />,
      p: (props) => <MarkdownParagraph {...props} openFrom={openFrom} />,
      table: MarkdownTable
    }),
    [isDead, openFrom]
  );
}

/* GFM gives us tables and strikethrough. Without it a table the model writes
   comes through as literal pipe characters in a paragraph. Module-level so the
   array reference stays stable across the streamed re-renders. */
const REMARK_PLUGINS = [remarkGfm];

/**
 * Render the streamed answer as formatted HTML (markdown → React, raw HTML
 * disabled). An optional `header` (the assistant identity) renders inside the
 * bubble, above the text.
 * While the answer is still being written the text is passed through
 * stabilizeStreamingMarkdown first, so half-arrived markup renders as what it
 * is going to be instead of flashing its raw characters and reflowing the lines
 * below it (see streamingMarkdown.js).
 * @param {{ text?: string, header?: import('react').ReactNode, components: import('react-markdown').Components, isStreaming?: boolean, openFrom?: { current: number|null } }} props
 */
function ChatbotText({ text, header, components, isStreaming = false, openFrom }) {
  if (!text?.trim()) return null;
  const markdown = isStreaming ? stabilizeStreamingMarkdown(text) : text;
  // Where the unfinished block ends, for the paragraph renderer to read as it
  // walks this subtree. Written here, one step above the Markdown element, so
  // the two cannot disagree: React renders depth-first, so the subtree below
  // sees this value before the next text part overwrites it.
  if (openFrom) openFrom.current = isStreaming ? markdown.length : null;
  return (
    <div className="Chatbot__text">
      {header}
      <Markdown components={components} remarkPlugins={REMARK_PLUGINS}>
        {markdown}
      </Markdown>
    </div>
  );
}

/**
 * Every tool whose call is worth surfacing above the answer as a lookup row,
 * mapped to the kind of lookup it performs. The kind decides which noun the row
 * names, so the visitor sees *which* of the assistant's lookups an answer rests
 * on — a structured event lookup reads differently from a plain site search.
 */
const LOOKUP_TOOLS = {
  'tool-searchContent': 'content',
  'tool-findEvents': 'events',
  'tool-findProducts': 'products'
};

/** The lookup kind behind a message part, or undefined when it is not a lookup. */
function lookupKind(part) {
  return typeof part?.type === 'string' ? LOOKUP_TOOLS[part.type] : undefined;
}

/**
 * The noun naming what a lookup searched through. searchContent *is* the site
 * search, so it gets no noun and its rows read exactly as they always have;
 * the two finder tools name themselves ("… arrangementer").
 */
function lookupNoun(kind, dictionary) {
  if (kind === 'events') return dictionary.searchEvents || 'events';
  if (kind === 'products') return dictionary.searchProducts || 'places and experiences';
  return '';
}

/**
 * The label for one tool call. The text search tools carry a single `query`;
 * findEvents carries a set of filters instead, so its label is built from the
 * ones the assistant actually applied ("Skien, desember"). Returns an empty
 * string when there is nothing worth showing — findEvents called with no filters
 * at all is a perfectly normal "what's on?" lookup, but not a search to name.
 */
function searchLabel(input) {
  if (!input || typeof input !== 'object') return '';
  const query = String(input.query ?? '').trim();
  if (query) return query;
  return [input.place, input.period, input.category]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

// Akka\Search::search returns nothing below two characters, so a link to the
// search page for a shorter query would land on an empty result set.
const MIN_QUERY_LENGTH = 2;

/**
 * The lookups this answer is grounded in, listed above the text. While a lookup
 * is running it shows as a plain "searching …" status; once the hits are in, a
 * lookup with a free-text query behind it becomes a link to the site's own
 * search page for that query, so the visitor can keep digging where the
 * assistant left off. The hits themselves are not rendered — the model links to
 * them inline in its answer, and matching posts surface in the response blurb
 * carousel below.
 *
 * Every lookup gets a row, including a findEvents call with no filters at all
 * ("Søkte etter arrangementer"): that the assistant went to the event calendar
 * is worth showing even when it narrowed nothing. Rows are deduplicated per tool
 * and label, since the model often runs the same phrase through more than one
 * tool within a single answer — and because each row names its tool, the two
 * rows that survive say different things.
 *
 * @param {{ parts: Array, dictionary: Record<string, string>, searchPageUrl: string }} props
 */
function ChatbotSearches({ parts, dictionary, searchPageUrl }) {
  const searches = [];
  const byRow = new Map();

  for (const part of parts) {
    const kind = lookupKind(part);
    if (!kind) continue;
    const label = searchLabel(part.input);
    // A content search is nothing but its query, so a missing or too-short one
    // leaves no row worth showing. The finder tools stand on their own.
    if (kind === 'content' && label.length < MIN_QUERY_LENGTH) continue;
    // Only a real free-text query has a meaningful site-search page behind it; a
    // findEvents label like "Skien, desember" would land on a poor result set.
    const linkable = String(part.input?.query ?? '').trim().length >= MIN_QUERY_LENGTH;

    const done = part.state === 'output-available';
    const key = `${kind}|${label}`;
    const seen = byRow.get(key);
    if (seen) {
      // A finished pass wins over a still-pending one for the same lookup.
      seen.done = seen.done || done;
      continue;
    }
    const search = { key, kind, label, done, linkable };
    byRow.set(key, search);
    searches.push(search);
  }

  if (!searches.length) return null;

  return (
    <div className="Chatbot__searches">
      {searches.map(({ key, kind, label, done, linkable }) => {
        // The noun stays in the link text too: without it, a query run through
        // both searchContent and findEvents would render two identical rows.
        const tail = [lookupNoun(kind, dictionary), label && `«${label}»`];

        if (done && linkable) {
          return (
            <Link key={key} className="Chatbot__search-link" href={`${searchPageUrl}?q=${encodeURIComponent(label)}`}>
              {[dictionary.searchLink || 'See all results for', ...tail].filter(Boolean).join(' ')}
            </Link>
          );
        }

        const verb = done
          ? dictionary.searched || dictionary.searchLink || 'Searched for'
          : dictionary.searching || 'Searching …';
        return (
          <div key={key} className={done ? 'Chatbot__searched' : 'Chatbot__searching'}>
            {[verb, ...tail].filter(Boolean).join(' ')}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The products/events/pages this answer links to, rendered as a horizontal blurb
 * slider beneath the text — so the visitor gets a tappable card (image, title,
 * date for events, address) for everything the assistant just recommended.
 * @param {{ posts: Array, dictionary: Record<string, string> }} props
 */
function ChatbotResponseBlurbs({ posts, dictionary }) {
  if (!posts.length) return null;

  return (
    <BlurbList.Slider className="Chatbot__response-blurbs">
      {posts.map((post) => (
        <Blurb variant="chatt" key={post.post_id ?? post.id ?? post.url}>
          <Blurb.Image src={post.image} alt={post.title} loading="lazy" />
          <Blurb.Link href={post.url || post.permalink} overlay>
            {post.title}
          </Blurb.Link>
          <Blurb.Content>
            <Blurb.Title level={3}>{post.title}</Blurb.Title>
            {post.excerpt && (
              <>
                <Blurb.Description>
                  <HtmlToJsx html={post.excerpt} />
                </Blurb.Description>
                <Blurb.CTA
                  primaryText={post.booking_url ? dictionary?.product?.bookNow || 'Bestill nå!' : ''}
                  primaryHref={post.booking_url}
                />
              </>
            )}
          </Blurb.Content>
        </Blurb>
      ))}
    </BlurbList.Slider>
  );
}

/**
 * Avatar + name shown inside the assistant's very first answer bubble, for a
 * personal touch. The avatar is the Telemark logo.
 * @param {{ name: string }} props
 */
function ChatbotIdentity({ name }) {
  return (
    <div className="Chatbot__identity">
      <TelemarkMark className="Chatbot__avatar" aria-hidden="true" />
      {name && <span className="Chatbot__assistant-name">{name}</span>}
    </div>
  );
}

/**
 * @param {{ message: object, dictionary?: Record<string, string>, lookup?: (href: string) => object|null, isDeadLink?: (href: string) => boolean, assistantName?: string, showIdentity?: boolean, isStreaming?: boolean }} props
 */
export default function ChatbotMessage({
  message,
  dictionary = {},
  lookup,
  isDeadLink,
  assistantName = '',
  showIdentity = false,
  isStreaming = false
}) {
  // Where the block still being streamed starts, for the paragraph renderer
  // (see MarkdownParagraph). A ref, so the component map stays stable.
  const openFrom = useRef(null);
  const markdownComponents = useMarkdownComponents(isDeadLink, openFrom);
  const isUser = message.role === 'user';
  // The search page is configured in WP (Settings → Search page); same source the
  // site header's search form reads (see SiteHeader.SearchForm.jsx).
  const { siteMeta } = useSiteConfig();
  const searchPageUrl = siteMeta?.settings?.search_page?.url || '/sok';
  // Match the inline links in the answer back to the loaded posts, so the cards
  // can carry image/date/address the model never sees (see chatbotMentions.js).
  const blurbs = isUser ? [] : mentionedPostsForMessage(message, lookup);
  // The identity (logo + name) lives inside the first text bubble of the answer.
  const showId = !isUser && showIdentity;
  const firstTextIndex = message.parts.findIndex((part) => part.type === 'text');
  // Only the part still being written needs the half-markup guard; the ones
  // before it are finished text, and running it on those is wasted work.
  const lastTextIndex = message.parts.findLastIndex((part) => part.type === 'text');

  return (
    <div className={`Chatbot__message Chatbot__message--${isUser ? 'user' : 'assistant'}`}>
      {!isUser && <ChatbotSearches parts={message.parts} dictionary={dictionary} searchPageUrl={searchPageUrl} />}
      {message.parts.map((part, index) => {
        if (part.type === 'text') {
          return (
            <ChatbotText
              key={index}
              text={part.text}
              components={markdownComponents}
              isStreaming={isStreaming && index === lastTextIndex}
              openFrom={openFrom}
              header={showId && index === firstTextIndex ? <ChatbotIdentity name={assistantName} /> : null}
            />
          );
        }
        // Search tool parts are rendered together above the answer
        // (see ChatbotSearches), not inline here.
        return null;
      })}
      <ChatbotResponseBlurbs posts={blurbs} dictionary={dictionary} />
    </div>
  );
}
